import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  useTheme,
  useMediaQuery,
  Divider,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  actions?: React.ReactNode;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  fullWidth?: boolean;
}

export const Modal: React.FC<ModalProps> = ({
  open,
  onClose,
  title,
  children,
  actions,
  maxWidth = 'sm',
  fullWidth = true,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      PaperProps={{
        sx: {
          borderRadius: 15,
          overflow: 'hidden',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 12px 48px rgba(0,0,0,0.15)',
          bgcolor: '#ffffff',
          minWidth: { xs: '95%', sm: '500px' },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: '#1976d2',
          color: 'white',
          px: 4,
          py: 3,
          flexShrink: 0,
          minHeight: 72,
          borderBottom: '1px solid rgba(255,255,255,0.08)',
        }}
      >
        <Typography variant="h6" fontWeight="600" sx={{ fontSize: '1.1rem', letterSpacing: '0.3px' }}>
          {title}
        </Typography>
        <IconButton 
          onClick={onClose} 
          sx={{ 
            color: 'white',
            '&:hover': {
              bgcolor: 'rgba(255,255,255,0.15)',
            },
          }} 
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          px: 4,
          pt: 15,
          pb: 4,
          flex: 1,
          overflow: 'auto',
          bgcolor: '#fafbfc',
          '&::-webkit-scrollbar': {
            width: 6,
          },
          '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
            borderRadius: 10,
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#c1c1c1',
            borderRadius: 10,
            '&:hover': {
              background: '#a8a8a8',
            },
          },
        }}
      >
        {children}
      </DialogContent>

      {actions && (
        <>
          <Divider sx={{ borderColor: 'rgba(0,0,0,0.06)' }} />
          <DialogActions 
            sx={{ 
              px: 4, 
              py: 2.5, 
              flexShrink: 0, 
              gap: 1.5,
              bgcolor: '#fafbfc',
            }}
          >
            {actions}
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export default Modal;
